import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { prospectsApi, Prospect, OutreachActivityItem, ProspectStats } from '../services/api';
import Swal from 'sweetalert2';
import styles from './Prospects.module.css';
import { getSequenceForSegment, fillTemplate, OutreachTouch } from '../data/outreachSequences';

const STAGES: { key: Prospect['stage']; label: string; color: string }[] = [
  { key: 'research', label: 'Research/Identified', color: '#6b7280' },
  { key: 'first_outreach', label: 'First Outreach', color: '#8b5cf6' },
  { key: 'discovery_call', label: 'Discovery Call', color: '#3b82f6' },
  { key: 'needs_analysis', label: 'Needs Analysis', color: '#06b6d4' },
  { key: 'proposal_sent', label: 'Proposal Sent', color: '#f59e0b' },
  { key: 'negotiation', label: 'Negotiation', color: '#f97316' },
  { key: 'closed_won', label: 'Closed Won', color: '#22c55e' },
  { key: 'closed_lost', label: 'Closed Lost', color: '#dc2626' },
];

const SEGMENTS: { key: Prospect['segment']; label: string }[] = [
  { key: 'ai_data_consultancy', label: 'AI/Data Consultancy' },
  { key: 'staff_aug', label: 'Staff Aug Firm' },
  { key: 'bank_credit_union', label: 'Bank/Credit Union' },
  { key: 'insurer', label: 'Insurer' },
  { key: 'it_integrator', label: 'IT Integrator' },
  { key: 'marketing_cx_agency', label: 'Marketing/CX Agency' },
];

const ACTIVITY_TYPES: { key: OutreachActivityItem['activityType']; label: string }[] = [
  { key: 'linkedin_dm', label: 'LinkedIn DM' },
  { key: 'email', label: 'Email' },
  { key: 'linkedin_engage', label: 'LinkedIn Engagement' },
  { key: 'call', label: 'Call' },
  { key: 'meeting', label: 'Meeting' },
  { key: 'other', label: 'Other' },
];

const CHANNELS: { key: OutreachActivityItem['channel']; label: string }[] = [
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'in_person', label: 'In-Person' },
  { key: 'other', label: 'Other' },
];

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function formatDate(date: string | null): string {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

export default function Prospects() {
  const { getIdToken } = useAuth();
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [stats, setStats] = useState<ProspectStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activities, setActivities] = useState<Record<string, OutreachActivityItem[]>>({});

  // Filters
  const [segmentFilter, setSegmentFilter] = useState<string>('');
  const [stageFilter, setStageFilter] = useState<string>('');

  useEffect(() => {
    loadData();
  }, [segmentFilter, stageFilter]);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);
      const token = await getIdToken();
      if (!token) {
        setError('Not authenticated');
        return;
      }

      const filters: { segment?: string; stage?: string } = {};
      if (segmentFilter) filters.segment = segmentFilter;
      if (stageFilter) filters.stage = stageFilter;

      const [prospectsResponse, statsResponse] = await Promise.all([
        prospectsApi.list(token, filters),
        prospectsApi.stats(token)
      ]);

      setProspects(prospectsResponse.items);
      setStats(statsResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prospects');
    } finally {
      setLoading(false);
    }
  }

  async function loadActivities(prospectId: string) {
    try {
      const token = await getIdToken();
      if (!token) return;

      const response = await prospectsApi.getActivity(token, prospectId);
      setActivities(prev => ({ ...prev, [prospectId]: response.activities }));
    } catch (err) {
      console.error('Failed to load activities:', err);
    }
  }

  async function handleExpand(prospectId: string) {
    if (expandedId === prospectId) {
      setExpandedId(null);
    } else {
      setExpandedId(prospectId);
      if (!activities[prospectId]) {
        await loadActivities(prospectId);
      }
    }
  }

  async function handleAddProspect() {
    const result = await Swal.fire({
      title: 'Add New Prospect',
      html: `
        <input id="swal-company" class="swal2-input" placeholder="Company name *">
        <input id="swal-city" class="swal2-input" placeholder="HQ City">
        <input id="swal-contact" class="swal2-input" placeholder="Contact name">
        <input id="swal-title" class="swal2-input" placeholder="Contact title">
        <select id="swal-segment" class="swal2-select">
          ${SEGMENTS.map(s => `<option value="${s.key}">${s.label}</option>`).join('')}
        </select>
        <select id="swal-maturity" class="swal2-select">
          <option value="">AI Maturity</option>
          <option value="exploring">Exploring</option>
          <option value="piloting">Piloting</option>
          <option value="in_production">In Production</option>
        </select>
        <input id="swal-leadsource" class="swal2-input" placeholder="Lead source">
        <input id="swal-linkedin" class="swal2-input" placeholder="LinkedIn URL">
        <input id="swal-website" class="swal2-input" placeholder="Website URL">
        <textarea id="swal-notes" class="swal2-textarea" placeholder="Notes"></textarea>
      `,
      showCancelButton: true,
      confirmButtonText: 'Create',
      preConfirm: () => {
        const company = (document.getElementById('swal-company') as HTMLInputElement).value.trim();
        if (!company) {
          Swal.showValidationMessage('Company name is required');
          return null;
        }

        return {
          company,
          hqCity: (document.getElementById('swal-city') as HTMLInputElement).value.trim() || null,
          contactName: (document.getElementById('swal-contact') as HTMLInputElement).value.trim() || null,
          contactTitle: (document.getElementById('swal-title') as HTMLInputElement).value.trim() || null,
          segment: (document.getElementById('swal-segment') as HTMLSelectElement).value as Prospect['segment'],
          aiMaturity: (document.getElementById('swal-maturity') as HTMLSelectElement).value || null,
          leadSource: (document.getElementById('swal-leadsource') as HTMLInputElement).value.trim() || null,
          linkedinUrl: (document.getElementById('swal-linkedin') as HTMLInputElement).value.trim() || null,
          websiteUrl: (document.getElementById('swal-website') as HTMLInputElement).value.trim() || null,
          notes: (document.getElementById('swal-notes') as HTMLTextAreaElement).value.trim() || null,
        };
      },
    });

    if (result.isConfirmed && result.value) {
      try {
        const token = await getIdToken();
        if (!token) return;

        const response = await prospectsApi.create(token, result.value);
        setProspects(prev => [...prev, response.item]);
        await loadData(); // Reload to update stats
        Swal.fire({ icon: 'success', title: 'Prospect created!', timer: 1500, showConfirmButton: false });
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err instanceof Error ? err.message : 'Failed to create prospect',
        });
      }
    }
  }

  async function handleEdit(prospect: Prospect) {
    const result = await Swal.fire({
      title: 'Edit Prospect',
      html: `
        <input id="swal-company" class="swal2-input" value="${prospect.company}" placeholder="Company name *">
        <input id="swal-city" class="swal2-input" value="${prospect.hqCity || ''}" placeholder="HQ City">
        <input id="swal-contact" class="swal2-input" value="${prospect.contactName || ''}" placeholder="Contact name">
        <input id="swal-title" class="swal2-input" value="${prospect.contactTitle || ''}" placeholder="Contact title">
        <select id="swal-segment" class="swal2-select">
          ${SEGMENTS.map(s => `<option value="${s.key}" ${prospect.segment === s.key ? 'selected' : ''}>${s.label}</option>`).join('')}
        </select>
        <select id="swal-maturity" class="swal2-select">
          <option value="">AI Maturity</option>
          <option value="exploring" ${prospect.aiMaturity === 'exploring' ? 'selected' : ''}>Exploring</option>
          <option value="piloting" ${prospect.aiMaturity === 'piloting' ? 'selected' : ''}>Piloting</option>
          <option value="in_production" ${prospect.aiMaturity === 'in_production' ? 'selected' : ''}>In Production</option>
        </select>
        <input id="swal-leadsource" class="swal2-input" value="${prospect.leadSource || ''}" placeholder="Lead source">
        <select id="swal-stage" class="swal2-select">
          ${STAGES.map(s => `<option value="${s.key}" ${prospect.stage === s.key ? 'selected' : ''}>${s.label}</option>`).join('')}
        </select>
        <input id="swal-value" class="swal2-input" type="number" value="${prospect.estDealValue || ''}" placeholder="Est. deal value ($)">
        <input id="swal-probability" class="swal2-input" type="number" min="0" max="100" value="${prospect.winProbability ? prospect.winProbability * 100 : ''}" placeholder="Win probability (%)">
        <input id="swal-tier" class="swal2-input" value="${prospect.tierOffered || ''}" placeholder="Tier offered">
        <input id="swal-nextaction" class="swal2-input" value="${prospect.nextAction || ''}" placeholder="Next action">
        <input id="swal-linkedin" class="swal2-input" value="${prospect.linkedinUrl || ''}" placeholder="LinkedIn URL">
        <input id="swal-website" class="swal2-input" value="${prospect.websiteUrl || ''}" placeholder="Website URL">
        <textarea id="swal-notes" class="swal2-textarea" placeholder="Notes">${prospect.notes || ''}</textarea>
      `,
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: 'Save',
      denyButtonText: 'Delete',
      denyButtonColor: '#dc2626',
      preConfirm: () => {
        const company = (document.getElementById('swal-company') as HTMLInputElement).value.trim();
        if (!company) {
          Swal.showValidationMessage('Company name is required');
          return null;
        }

        const winProbValue = (document.getElementById('swal-probability') as HTMLInputElement).value;
        const winProbability = winProbValue ? parseFloat(winProbValue) / 100 : null;

        return {
          company,
          hqCity: (document.getElementById('swal-city') as HTMLInputElement).value.trim() || null,
          contactName: (document.getElementById('swal-contact') as HTMLInputElement).value.trim() || null,
          contactTitle: (document.getElementById('swal-title') as HTMLInputElement).value.trim() || null,
          segment: (document.getElementById('swal-segment') as HTMLSelectElement).value as Prospect['segment'],
          aiMaturity: (document.getElementById('swal-maturity') as HTMLSelectElement).value || null,
          leadSource: (document.getElementById('swal-leadsource') as HTMLInputElement).value.trim() || null,
          stage: (document.getElementById('swal-stage') as HTMLSelectElement).value as Prospect['stage'],
          estDealValue: (document.getElementById('swal-value') as HTMLInputElement).value
            ? parseFloat((document.getElementById('swal-value') as HTMLInputElement).value)
            : null,
          winProbability,
          tierOffered: (document.getElementById('swal-tier') as HTMLInputElement).value.trim() || null,
          nextAction: (document.getElementById('swal-nextaction') as HTMLInputElement).value.trim() || null,
          linkedinUrl: (document.getElementById('swal-linkedin') as HTMLInputElement).value.trim() || null,
          websiteUrl: (document.getElementById('swal-website') as HTMLInputElement).value.trim() || null,
          notes: (document.getElementById('swal-notes') as HTMLTextAreaElement).value.trim() || null,
        };
      },
    });

    if (result.isConfirmed && result.value) {
      try {
        const token = await getIdToken();
        if (!token) return;

        const response = await prospectsApi.update(token, prospect.id, result.value);
        setProspects(prev => prev.map(p => p.id === prospect.id ? response.item : p));
        await loadData(); // Reload to update stats
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err instanceof Error ? err.message : 'Failed to update prospect',
        });
      }
    } else if (result.isDenied) {
      const confirm = await Swal.fire({
        icon: 'warning',
        title: 'Delete this prospect?',
        text: prospect.company,
        showCancelButton: true,
        confirmButtonText: 'Delete',
        confirmButtonColor: '#dc2626',
      });

      if (confirm.isConfirmed) {
        try {
          const token = await getIdToken();
          if (!token) return;

          await prospectsApi.delete(token, prospect.id);
          setProspects(prev => prev.filter(p => p.id !== prospect.id));
          await loadData(); // Reload to update stats
        } catch (err) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: err instanceof Error ? err.message : 'Failed to delete prospect',
          });
        }
      }
    }
  }

  async function handleLogActivity(prospect: Prospect) {
    const result = await Swal.fire({
      title: 'Log Activity',
      html: `
        <select id="swal-type" class="swal2-select">
          ${ACTIVITY_TYPES.map(t => `<option value="${t.key}">${t.label}</option>`).join('')}
        </select>
        <select id="swal-channel" class="swal2-select">
          ${CHANNELS.map(c => `<option value="${c.key}">${c.label}</option>`).join('')}
        </select>
        <textarea id="swal-summary" class="swal2-textarea" placeholder="Summary of activity *"></textarea>
        <input id="swal-nextstep" class="swal2-input" placeholder="Next step">
        <input id="swal-nextstepdate" class="swal2-input" type="date" placeholder="Next step date">
      `,
      showCancelButton: true,
      confirmButtonText: 'Log Activity',
      preConfirm: () => {
        const summary = (document.getElementById('swal-summary') as HTMLTextAreaElement).value.trim();
        if (!summary) {
          Swal.showValidationMessage('Summary is required');
          return null;
        }

        return {
          activityType: (document.getElementById('swal-type') as HTMLSelectElement).value as OutreachActivityItem['activityType'],
          channel: (document.getElementById('swal-channel') as HTMLSelectElement).value as OutreachActivityItem['channel'],
          summary,
          nextStep: (document.getElementById('swal-nextstep') as HTMLInputElement).value.trim() || undefined,
          nextStepDate: (document.getElementById('swal-nextstepdate') as HTMLInputElement).value || undefined,
        };
      },
    });

    if (result.isConfirmed && result.value) {
      try {
        const token = await getIdToken();
        if (!token) return;

        const response = await prospectsApi.logActivity(token, prospect.id, result.value);

        // Update activities in state
        setActivities(prev => ({
          ...prev,
          [prospect.id]: [response.activity, ...(prev[prospect.id] || [])]
        }));

        // Update prospect's lastTouchDate and nextAction
        const updatedProspect = prospects.find(p => p.id === prospect.id);
        if (updatedProspect) {
          updatedProspect.lastTouchDate = response.activity.date;
          if (result.value.nextStep) {
            updatedProspect.nextAction = result.value.nextStep;
          }
          setProspects([...prospects]);
        }

        Swal.fire({ icon: 'success', title: 'Activity logged!', timer: 1500, showConfirmButton: false });
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err instanceof Error ? err.message : 'Failed to log activity',
        });
      }
    }
  }

  async function handleMove(prospect: Prospect, newStage: Prospect['stage']) {
    try {
      const token = await getIdToken();
      if (!token) return;

      const response = await prospectsApi.move(token, prospect.id, newStage);
      setProspects(prev => prev.map(p => p.id === prospect.id ? response.item : p));
      await loadData(); // Reload to update stats
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err instanceof Error ? err.message : 'Failed to move prospect',
      });
    }
  }

  const getStageInfo = (stage: Prospect['stage']) => STAGES.find(s => s.key === stage);
  const getSegmentLabel = (segment: Prospect['segment']) => SEGMENTS.find(s => s.key === segment)?.label || segment;

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading prospects...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>{error}</div>
        <button onClick={loadData} className={styles.retryBtn}>Retry</button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1>Prospects</h1>
        <button className={styles.addBtn} onClick={handleAddProspect}>
          + Add Prospect
        </button>
      </header>

      {stats && (
        <div className={styles.summaryBar}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Total Prospects</span>
            <span className={styles.summaryValue}>{stats.totalProspects}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Pipeline Value</span>
            <span className={styles.summaryValue}>{formatCurrency(stats.totalValue)}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>Weighted Pipeline</span>
            <span className={styles.summaryValue}>{formatCurrency(stats.weightedValue)}</span>
          </div>
        </div>
      )}

      <div className={styles.filters}>
        <select
          className={styles.filterSelect}
          value={segmentFilter}
          onChange={e => setSegmentFilter(e.target.value)}
        >
          <option value="">All Segments</option>
          {SEGMENTS.map(s => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>

        <select
          className={styles.filterSelect}
          value={stageFilter}
          onChange={e => setStageFilter(e.target.value)}
        >
          <option value="">All Stages</option>
          {STAGES.map(s => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Company</th>
              <th>Contact</th>
              <th>Segment</th>
              <th>Stage</th>
              <th>Last Touch</th>
              <th>Next Action</th>
              <th>Est. Value</th>
              <th>Win Prob</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {prospects.map(prospect => {
              const stageInfo = getStageInfo(prospect.stage);
              const isExpanded = expandedId === prospect.id;
              const prospectActivities = activities[prospect.id] || [];

              return (
                <>
                  <tr
                    key={prospect.id}
                    className={`${styles.row} ${isExpanded ? styles.expanded : ''}`}
                    onClick={() => handleExpand(prospect.id)}
                  >
                    <td className={styles.company}>
                      {prospect.websiteUrl ? (
                        <a href={prospect.websiteUrl} target="_blank" rel="noopener noreferrer" className={styles.companyLink} onClick={(e) => e.stopPropagation()}>
                          {prospect.company} <span className={styles.linkIcon}>🔗</span>
                        </a>
                      ) : (
                        prospect.company
                      )}
                      {prospect.hqCity && <div className={styles.city}>{prospect.hqCity}</div>}
                    </td>
                    <td>
                      {prospect.contactName || '—'}
                      {prospect.contactTitle && <div className={styles.title}>{prospect.contactTitle}</div>}
                    </td>
                    <td>{getSegmentLabel(prospect.segment)}</td>
                    <td>
                      <span
                        className={styles.stageBadge}
                        style={{ backgroundColor: stageInfo?.color }}
                      >
                        {stageInfo?.label}
                      </span>
                    </td>
                    <td>{formatDate(prospect.lastTouchDate)}</td>
                    <td className={styles.nextAction}>{prospect.nextAction || '—'}</td>
                    <td>{prospect.estDealValue ? formatCurrency(prospect.estDealValue) : '—'}</td>
                    <td>{prospect.winProbability ? formatPercent(prospect.winProbability) : '—'}</td>
                    <td className={styles.actions} onClick={e => e.stopPropagation()}>
                      <button
                        className={styles.actionBtn}
                        onClick={() => handleLogActivity(prospect)}
                      >
                        Log
                      </button>
                      <button
                        className={styles.actionBtn}
                        onClick={() => handleEdit(prospect)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className={styles.expandedRow}>
                      <td colSpan={9}>
                        <div className={styles.expandedContent}>
                          <div className={styles.prospectDetails}>
                            <div className={styles.detailsGrid}>
                              {prospect.leadSource && (
                                <div className={styles.detail}>
                                  <strong>Lead Source:</strong> {prospect.leadSource}
                                </div>
                              )}
                              {prospect.aiMaturity && (
                                <div className={styles.detail}>
                                  <strong>AI Maturity:</strong> {prospect.aiMaturity}
                                </div>
                              )}
                              {prospect.tierOffered && (
                                <div className={styles.detail}>
                                  <strong>Tier Offered:</strong> {prospect.tierOffered}
                                </div>
                              )}
                              {prospect.linkedinUrl && (
                                <div className={styles.detail}>
                                  <strong>LinkedIn:</strong>{' '}
                                  <a href={prospect.linkedinUrl} target="_blank" rel="noopener noreferrer">
                                    View Profile
                                  </a>
                                </div>
                              )}
                              {prospect.websiteUrl && (
                                <div className={styles.detail}>
                                  <strong>Website:</strong>{' '}
                                  <a href={prospect.websiteUrl} target="_blank" rel="noopener noreferrer">
                                    Visit Site
                                  </a>
                                </div>
                              )}
                            </div>
                            {prospect.notes && (
                              <div className={styles.notes}>
                                <strong>Notes:</strong>
                                <p>{prospect.notes}</p>
                              </div>
                            )}
                          </div>

                          <div className={styles.stageActions}>
                            <strong>Move to:</strong>
                            <div className={styles.moveButtons}>
                              {STAGES.filter(s => s.key !== prospect.stage).map(s => (
                                <button
                                  key={s.key}
                                  className={styles.moveBtn}
                                  onClick={() => handleMove(prospect, s.key)}
                                >
                                  {s.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Suggested Next Touch */}
                          {(() => {
                            const sequence = getSequenceForSegment(prospect.segment);
                            if (!sequence) return null;
                            const touchIndex = prospectActivities.length;
                            const nextTouch: OutreachTouch | undefined = sequence.touches[touchIndex];
                            const channelIcons: Record<string, string> = {
                              linkedin_dm: '💬',
                              email: '📧',
                              linkedin_engage: '👍',
                            };
                            const channelLabels: Record<string, string> = {
                              linkedin_dm: 'LinkedIn DM',
                              email: 'Email',
                              linkedin_engage: 'LinkedIn Engage',
                            };

                            if (!nextTouch) {
                              return (
                                <div className={styles.suggestedTouch}>
                                  <div className={styles.suggestedTouchHeader}>
                                    <span className={styles.suggestedTouchTitle}>✅ Sequence Complete</span>
                                  </div>
                                  <p className={styles.suggestedTouchComplete}>
                                    All {sequence.touches.length} touches completed — add to monthly nurture list
                                  </p>
                                </div>
                              );
                            }

                            const filledTemplate = fillTemplate(
                              nextTouch.template,
                              prospect.company,
                              prospect.contactName
                            );
                            const filledSubject = nextTouch.subject
                              ? fillTemplate(nextTouch.subject, prospect.company, prospect.contactName)
                              : null;

                            return (
                              <div className={styles.suggestedTouch}>
                                <div className={styles.suggestedTouchHeader}>
                                  <span className={styles.suggestedTouchTitle}>📋 Suggested Next Touch</span>
                                  <span className={styles.suggestedTouchMeta}>
                                    Touch {nextTouch.touchNumber} of {sequence.touches.length}
                                  </span>
                                </div>
                                <div className={styles.suggestedTouchDetails}>
                                  <span className={styles.touchChannel}>
                                    {channelIcons[nextTouch.channel]} {channelLabels[nextTouch.channel]}
                                  </span>
                                  <span className={styles.touchTiming}>Day {nextTouch.dayOffset}</span>
                                  {filledSubject && (
                                    <span className={styles.touchSubject}>
                                      Subject: <strong>{filledSubject}</strong>
                                    </span>
                                  )}
                                </div>
                                <div className={styles.suggestedTouchTemplate}>
                                  {filledTemplate}
                                </div>
                                <button
                                  className={styles.copyBtn}
                                  onClick={() => {
                                    const textToCopy = filledSubject
                                      ? `Subject: ${filledSubject}\n\n${filledTemplate}`
                                      : filledTemplate;
                                    navigator.clipboard.writeText(textToCopy);
                                    Swal.fire({
                                      icon: 'success',
                                      title: 'Copied!',
                                      timer: 1000,
                                      showConfirmButton: false,
                                      toast: true,
                                      position: 'top-end',
                                    });
                                  }}
                                >
                                  📋 Copy to Clipboard
                                </button>
                              </div>
                            );
                          })()}

                          {prospectActivities.length > 0 && (
                            <div className={styles.activityLog}>
                              <h4>Activity Log</h4>
                              {prospectActivities.map(activity => (
                                <div key={activity.id} className={styles.activity}>
                                  <div className={styles.activityHeader}>
                                    <span className={styles.activityDate}>
                                      {new Date(activity.date).toLocaleDateString()}
                                    </span>
                                    <span className={styles.activityType}>
                                      {ACTIVITY_TYPES.find(t => t.key === activity.activityType)?.label}
                                    </span>
                                    <span className={styles.activityChannel}>
                                      via {CHANNELS.find(c => c.key === activity.channel)?.label}
                                    </span>
                                  </div>
                                  <div className={styles.activitySummary}>{activity.summary}</div>
                                  {activity.nextStep && (
                                    <div className={styles.activityNext}>
                                      <strong>Next:</strong> {activity.nextStep}
                                      {activity.nextStepDate && ` by ${formatDate(activity.nextStepDate)}`}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>

        {prospects.length === 0 && (
          <div className={styles.empty}>
            No prospects found. Click "Add Prospect" to get started.
          </div>
        )}
      </div>
    </div>
  );
}